﻿import { Gesture } from "@use-gesture/vanilla";

import anime from "animejs";

function handleWheel(els, state, options) {
    return ({ event, active, movement: [scrollX, scrollY], ...rest }) => {
        event.preventDefault();
        if (active) {
            const newScale = state.scale - scrollY / 2000;
            // Bound scale
            state.scale = Math.max(Math.min(newScale, options.maxScale), options.minScale);
        }

        anime({
            targets: els.element,
            scale: state.scale,
            duration: options.defaultDuration,
            easing: options.defaultEasing,
        });
    };
}

function handlePinch(els, state, options) {
    return ({ active, distance: [dist], delta: [d], ...rest }) => {
        if (active) {
            // Stabilises with panning, helps to prevent jitteryness
            const stableFactor = 500;
            const rounded = Math.round(d * stableFactor) / stableFactor;
            const newScale = state.scale + rounded / 5;
            // Bound scale
            state.scale = Math.max(Math.min(newScale, options.maxScale), options.minScale);
        }

        anime({
            targets: els.element,
            scale: state.scale,
            duration: options.defaultDuration,
            easing: options.defaultEasing,
        });
    };
}

function handleDrag(els, state, options, setInfoMessageVisibility = () => { }) {
    return ({ active, movement: [mx, my], delta: [deltaX, deltaY], touches, pinching, cancel, ...rest }) => {
        const scaledImageWidth = els.initialElementWidth * state.scale;
        const scaledImageHeight = els.initialElementHeight * state.scale;

        state.correcting = false;
        state.duration = options.defaultDuration;
        state.easing = options.defaultEasing;

        if (active) {
            if (state.isMobile && touches < 2) {
                setInfoMessageVisibility(true);
                return;
            } else if (state.isMobile && touches >= 2) {
                setInfoMessageVisibility(false);
            }

            // state.dx += deltaX / (state.isMobile ? 1.25 : 1);
            // state.dy += deltaY / (state.isMobile ? 1.25 : 1);
            state.dx += deltaX;
            state.dy += deltaY;
        }

        if (!active) {
            // To calculate new edges
            const horizontalDiff = scaledImageWidth - els.initialElementWidth;
            const verticalDiff = scaledImageHeight - els.initialElementHeight;

            // Find edges
            const leftEdge = state.dx - horizontalDiff / 2;
            const rightEdge = leftEdge + scaledImageWidth;
            const topEdge = state.dy - verticalDiff / 2;
            const bottomEdge = topEdge + scaledImageHeight;

            // Check edges opposite to boundaries to ensure is inside bounds
            // X checks
            if (leftEdge > state.boundaries.x.max - options.safeAreaDistance) {
                state.correcting = true;
                state.dx = state.boundaries.x.max + horizontalDiff / 2 - options.safeAreaDistance;
            } else if (rightEdge < state.boundaries.x.min + options.safeAreaDistance) {
                state.correcting = true;
                state.dx = state.boundaries.x.min - (scaledImageWidth - horizontalDiff / 2) + options.safeAreaDistance;
            }

            // Y checks
            if (topEdge > state.boundaries.y.max - options.safeAreaDistance) {
                state.correcting = true;
                state.dy = state.boundaries.y.max + verticalDiff / 2 - options.safeAreaDistance;
            } else if (bottomEdge < state.boundaries.y.min + options.safeAreaDistance) {
                state.correcting = true;
                state.dy = state.boundaries.y.min - (scaledImageHeight - verticalDiff / 2) + options.safeAreaDistance;
            }

            // Animate props
            if (state.correcting) {
                state.duration = options.correctingDuration;
                state.easing = options.correctingEasing;
            }
        }

        // Move element
        anime({
            targets: els.element,
            translateX: state.dx,
            translateY: state.dy,
            duration: state.duration,
            easing: state.easing,
        });
    };
}

const calculateBoundaries = (container) => {
    const containerDims = window.getComputedStyle(container);

    const containerWidth = container.offsetWidth;
    const containerHeight = container.offsetHeight;

    const containerPadLeft = parseFloat(containerDims.paddingLeft);
    const containerPadTop = parseFloat(containerDims.paddingTop);

    const minX = -containerPadLeft;
    const maxX = containerWidth - containerPadLeft;

    const maxY = containerHeight - containerPadTop;
    const minY = -containerPadTop;

    return {
        x: {
            min: minX,
            max: maxX,
        },
        y: {
            min: minY,
            max: maxY,
        },
    };
};

export default function (element, opts = {}) {
    const defaultOptions = {
        minScale: 0.25,
        maxScale: 2,
        safeAreaDistance: 100,
        defaultEasing: "linear",
        correctingEasing: "easeInOutQuad",
        defaultDuration: 0,
        correctingDuration: 350,
        mobileBreakpoint: 1024,
        panEnabled: true,
        wheelEnabled: null,
        pinchEnabled: null,
        zoomInButton: null,
        zoomOutButton: null,
        infoMessage: null,
    };

    const els = {
        element,
        container: element.parentElement,
        initialElementWidth: element.offsetWidth,
        initialElementHeight: element.offsetHeight,
    };

    const options = {
        ...defaultOptions,
        ...opts,
    };

    options.wheelEnabled = window.innerWidth >= options.mobileBreakpoint;
    options.pinchEnabled = window.innerWidth < options.mobileBreakpoint;

    const state = {
        dx: 0,
        dy: 0,
        scale: 1,
        easing: options.defaultEasing,
        correcting: false,
        duration: options.defaultDuration,
        boundaries: calculateBoundaries(els.container),
        isMobile: window.innerWidth < options.mobileBreakpoint,
    };

    const setInfoMessageVisibility = (visible) => {
        if (state.isMobile && options.infoMessage) {
            if (visible) {
                options.infoMessage.style.opacity = 1;
                options.infoMessage.style.visibility = "visible";
            } else {
                options.infoMessage.style.opacity = 0;
                options.infoMessage.style.visibility = "hidden";
            }
        }
    };

    const allGestures = new Gesture(
        els.element,
        {
            onDrag: options.panEnabled ? handleDrag(els, state, options, setInfoMessageVisibility) : function () { },
            onPinch: options.pinchEnabled ? handlePinch(els, state, options) : function () { },
            onWheel: options.wheelEnabled ? handleWheel(els, state, options) : function () { },
        },
        { eventOptions: { passive: false } }
    );

    const setStyles = () => {
        if (els.element.tagName.toLowerCase() === 'img') {
            els.element.setAttribute('draggable', false);
        } else {
            // Find any child images
            const imgs = els.element.querySelectorAll('img');
            imgs.forEach(function (img) {
                img.setAttribute('draggable', false);
            });
         
        }

        if (state.isMobile) {
            // Allow scroll on mobile
            els.element.style.touchAction = "pan-y";
            els.container.style.touchAction = "pan-y";
        } else {
            els.element.style.touchAction = "none";
            els.container.style.touchAction = "auto";

            if (options.infoMessage) {
                options.infoMessage.style.opacity = 0;
                options.infoMessage.style.visibility = "hidden";
            }
        }
    };

    const init = () => {
        window.addEventListener("resize", function () {
            state.isMobile = window.innerWidth < options.mobileBreakpoint;
            state.boundaries = calculateBoundaries(els.container);

            setStyles();
        });

        // Optional controls
        if (options.zoomInButton) {
            options.zoomInButton.addEventListener("click", function () {
                state.scale = Math.min(state.scale + 0.25, options.maxScale);

                anime({
                    targets: els.element,
                    scale: state.scale,
                    duration: options.correctingDuration,
                    easing: options.correctingEasing,
                });
            });
        }
        if (options.zoomOutButton) {
            options.zoomOutButton.addEventListener("click", function () {
                state.scale = Math.max(state.scale - 0.25, options.minScale);

                anime({
                    targets: els.element,
                    scale: state.scale,
                    duration: options.correctingDuration,
                    easing: options.correctingEasing,
                });
            });
        }
        setStyles();
    };

    init();
}
